import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, addDays, subDays, isToday, parse,
} from "date-fns";
import { ChevronLeft, ChevronRight, Upload, Loader2, Clock, MapPin, Ticket, Trash2, Eye, EyeOff, Undo2, AlertTriangle, Plus, Pencil, Cake, X, Users, StickyNote, CalendarDays, SlidersHorizontal, CalendarCheck, CalendarOff } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { fileToResizedDataUrl } from "@/lib/image";
import { ShiftEditor } from "@/components/ShiftEditor";
import { ShiftNoteDialog } from "@/components/ShiftNoteDialog";
import { tourTitle, dayScheduleFor, hoursBetween, isFutureRequest } from "@/lib/fields";
import { CallOutButton, CallOutDialog, isTodayShift } from "@/components/CallOut";
import { ScheduleReviewDialog } from "@/components/ScheduleReviewDialog";
import { useTheme } from "@/contexts/ThemeContext";

const parseTourDate = (s) => {
  if (!s) return null;
  const d = parse(String(s), "MM/dd/yyyy", new Date());
  return isNaN(d.getTime()) ? null : d;
};

const fileToBase64 = (file) => fileToResizedDataUrl(file, 1568, 0.82);

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const isStandby = (s) => s?.kind === "standby" || /STANDBY/i.test(s?.label || "");
const isTourShift = (s) => s?.kind === "tour";
const cleanTourLabel = (s) => (s?.label || "Tour").replace(/\s*\(.*\)$/, "");

export const MonthCalendar = ({ tours, onToursChanged, focusDate, onConvert }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [cursor, setCursor] = useState(new Date());
  const touchStart = useRef(null);
  const [shifts, setShifts] = useState([]);
  const [callouts, setCallouts] = useState([]);
  const [coEdit, setCoEdit] = useState({ open: false, initial: null });
  const [shiftsLoaded, setShiftsLoaded] = useState(false);
  const { resolved } = useTheme();
  const dark = resolved !== "light";
  const [tipDismissed, setTipDismissed] = useState(() => localStorage.getItem("sd_calendar_tip_dismissed") === "1");
  const dismissTip = () => { localStorage.setItem("sd_calendar_tip_dismissed", "1"); setTipDismissed(true); };
  const [importing, setImporting] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [dayKey, setDayKey] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [review, setReview] = useState({ open: false, mode: "preview", changes: null, pending: null });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [noteShift, setNoteShift] = useState(null);
  const [shiftEditorOpen, setShiftEditorOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("calendarView") || "week");
  useEffect(() => { localStorage.setItem("calendarView", view); }, [view]);
  const [filters, setFilters] = useState(() => {
    const def = { tours: true, requests: true, standby: true, shifts: true };
    try { return { ...def, ...JSON.parse(localStorage.getItem("calFilters") || "{}") }; } catch { return def; }
  });
  const setFilter = (k, v) => setFilters((f) => { const nf = { ...f, [k]: v }; try { localStorage.setItem("calFilters", JSON.stringify(nf)); } catch { /* ignore */ } return nf; });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const loadShifts = useCallback(async () => {
    try {
      const [sh, co] = await Promise.all([api.listShifts(), api.listCallouts().catch(() => [])]);
      setShifts(sh);
      setCallouts(Array.isArray(co) ? co : []);
    } catch { /* ignore */ } finally { setShiftsLoaded(true); }
  }, []);
  useEffect(() => { loadShifts(); }, [loadShifts]);
  useEffect(() => {
    const h = () => { loadShifts(); onToursChanged && onToursChanged(); };
    window.addEventListener("schedule-invalidate", h);
    return () => window.removeEventListener("schedule-invalidate", h);
  }, [loadShifts, onToursChanged]);
  const reloadAll = () => { loadShifts(); onToursChanged && onToursChanged(); };
  const keyToMdy = (k) => { try { return format(parse(k, "yyyy-MM-dd", new Date()), "M/d/yyyy"); } catch { return k; } };

  // Guest name -> celebrations (month/day/type), to flag tours that land on a birthday/anniversary.
  const [celebMap, setCelebMap] = useState({});
  useEffect(() => { api.celebrationsMap().then((m) => setCelebMap(m || {})).catch(() => {}); }, []);
  // Celebrations for a tour's guests that fall on the given calendar day (matches month+day).
  const celebsForTour = useCallback((t, dayObj) => {
    if (!dayObj) return [];
    const mo = dayObj.getMonth() + 1, d = dayObj.getDate();
    const names = [t.guest_name, ...(Array.isArray(t.linked_guests) ? t.linked_guests : [])].filter(Boolean);
    const hits = [];
    names.forEach((n) => (celebMap[n] || []).forEach((c) => { if (c.month === mo && c.day === d) hits.push(c); }));
    return hits;
  }, [celebMap]);
  const dayHasCelebration = useCallback((key) => {
    const dObj = parse(key, "yyyy-MM-dd", new Date());
    return (tours || []).some((t) => {
      const dates = Array.isArray(t.tour_date) ? t.tour_date : t.tour_date ? [t.tour_date] : [];
      const onThisDay = dates.some((ds) => { const pd = parseTourDate(ds); return pd && format(pd, "yyyy-MM-dd") === key; });
      return onThisDay && celebsForTour(t, dObj).length > 0;
    });
  }, [tours, celebsForTour]);

  // When opened from the Home "Next Shift" card, focus that date (select the day + scroll its month/week into view).
  useEffect(() => {
    if (focusDate instanceof Date && !isNaN(focusDate.getTime())) {
      setCursor(focusDate);
      setDayKey(format(focusDate, "yyyy-MM-dd"));
    }
  }, [focusDate]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/(png|jpe?g|webp)/i.test(file.type)) { toast.error("Upload a PNG, JPG or WEBP"); return; }
    setImporting(true);
    try {
      const dataUrl = await fileToBase64(file);
      const { shifts: found, is_schedule } = await api.extractSchedule(dataUrl);
      if (is_schedule === false) {
        toast.error("That looks like a tour sheet, not a work schedule. Upload it from the Tours tab to add it as a tour.", { duration: 8000 });
        return;
      }
      const valid = (found || []).filter((s) => s.date && String(s.date).trim());
      if (!valid.length) { toast.error("No shifts found in that screenshot"); return; }
      const res = await api.previewSchedule(valid);
      setReview({ open: true, mode: "preview", changes: res.changes, pending: valid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that schedule");
    } finally { setImporting(false); }
  };

  const applyPendingSchedule = async (picks, delIds) => {
    if (!review.pending) { setReview((s) => ({ ...s, open: false })); return; }
    setReviewBusy(true);
    try {
      const res = await api.applySchedule(review.pending, picks, delIds);
      const bits = [];
      if (res.added) bits.push(`${res.added} added`);
      if (res.changed) bits.push(`${res.changed} changed`);
      if (res.deleted) bits.push(`${res.deleted} deleted`);
      loadShifts();
      onToursChanged && onToursChanged();
      setReview((s) => ({ ...s, open: false }));
      toast.success(bits.length ? `Schedule imported — ${bits.join(", ")}` : "Schedule already up to date", {
        duration: 10000,
        action: { label: "Undo", onClick: () => undoImport() },
      });
    } catch {
      toast.error("Import failed");
    } finally { setReviewBusy(false); }
  };

  const undoImport = async () => {
    try {
      await api.undoSchedule();
      loadShifts();
      onToursChanged && onToursChanged();
      setConflicts([]);
      toast.success("Last import undone");
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Nothing to undo" : "Undo failed");
    }
  };

  const removeShift = async (id) => {
    try { await api.deleteShift(id); loadShifts(); toast.success("Shift removed"); } catch { toast.error("Delete failed"); }
  };

  const doDelete = async () => {
    const t = confirmDel;
    if (!t) return;
    try {
      if (t.kind === "shift") {
        await api.deleteShift(t.id);
        toast.success("Shift deleted");
      } else {
        await api.deleteGuest(t.id);
        toast.success("Tour moved to Trash", {
          duration: 8000,
          action: {
            label: "Undo",
            onClick: async () => {
              try { await api.restoreGuest(t.id); toast.success("Tour restored"); loadShifts(); onToursChanged && onToursChanged(); }
              catch { toast.error("Couldn't restore tour"); }
            },
          },
        });
      }
      loadShifts();
      onToursChanged && onToursChanged();
    } catch { toast.error("Delete failed"); }
    finally { setConfirmDel(null); }
  };

  const DelBtn = ({ kind, id, label, testid }) => (
    <button
      onClick={(e) => { e.stopPropagation(); setConfirmDel({ kind, id, label }); }}
      className="flex shrink-0 items-center rounded-md px-1.5 text-zinc-600 transition-colors hover:text-red-400"
      data-testid={testid}
      aria-label="Delete"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );

  const EditBtn = ({ shift, testid }) => (
    <button
      onClick={(e) => { e.stopPropagation(); openEditShift(shift); }}
      className="flex shrink-0 items-center rounded-md px-1.5 text-zinc-500 transition-colors hover:text-zinc-200"
      data-testid={testid}
      aria-label="Edit shift"
    >
      <Pencil className="h-3.5 w-3.5" />
    </button>
  );

  const promoteShift = async (id) => {
    try {
      await api.promoteShift(id);
      toast.success("Shift converted to a tour");
      loadShifts();
      onToursChanged && onToursChanged();
    } catch { toast.error("Convert failed"); }
  };

  const openNewShift = () => { setEditingShift(null); setShiftEditorOpen(true); };
  const openEditShift = (s) => { setEditingShift(s); setShiftEditorOpen(true); };
  const onShiftSaved = () => { loadShifts(); onToursChanged && onToursChanged(); };

  // Open the new-shift editor when triggered from the global Quick-Add FAB.
  useEffect(() => {
    const open = () => { setEditingShift(null); setShiftEditorOpen(true); };
    if (sessionStorage.getItem("sd:openNewShift") === "1") { sessionStorage.removeItem("sd:openNewShift"); open(); }
    window.addEventListener("sd:new-shift", open);
    return () => window.removeEventListener("sd:new-shift", open);
  }, []);

  // Category filter (from the toolbar dropdown) applied at the source.
  const shiftCategory = (s) => isTourShift(s) ? "tours" : isStandby(s) ? "standby" : "shifts";
  const visibleShifts = shifts.filter((s) => filters[shiftCategory(s)]);
  const visibleTours = tours.filter((t) => isFutureRequest(t) ? filters.requests : filters.tours);

  // Map yyyy-MM-dd -> tours
  const byDay = {};
  visibleTours.forEach((t) => {
    const dates = Array.isArray(t.tour_date) ? t.tour_date : t.tour_date ? [t.tour_date] : [];
    dates.forEach((ds) => {
      const d = parseTourDate(ds);
      if (!d) return;
      const key = format(d, "yyyy-MM-dd");
      (byDay[key] = byDay[key] || []).push(t);
    });
  });
  const shiftsByDay = {};
  visibleShifts.forEach((s) => {
    const d = parseTourDate(s.date);
    if (!d) return;
    const key = format(d, "yyyy-MM-dd");
    (shiftsByDay[key] = shiftsByDay[key] || []).push(s);
  });
  // Map yyyy-MM-dd -> call-outs (Sick / FMLA / Vacation / ADO / …) shown as small pills.
  const calloutsByDay = {};
  callouts.forEach((c) => {
    const d = c.date_iso ? parse(c.date_iso, "yyyy-MM-dd", new Date()) : parseTourDate(c.date);
    if (!d || isNaN(d)) return;
    const key = format(d, "yyyy-MM-dd");
    (calloutsByDay[key] = calloutsByDay[key] || []).push(c);
  });

  const monthStart = startOfMonth(cursor);
  const days = eachDayOfInterval({
    start: startOfWeek(monthStart),
    end: endOfWeek(endOfMonth(cursor)),
  });
  // Week view = an 8-day window starting yesterday (relative to the cursor).
  const weekStart = startOfWeek(cursor, { weekStartsOn: 0 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) });

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const upcoming = [];
  visibleShifts.forEach((s) => {
    const d = parseTourDate(s.date);
    if (d && d >= todayStart) upcoming.push({ date: d, kind: "shift", data: s });
  });
  tours.forEach((t) => {
    if (isFutureRequest(t) ? !filters.requests : !filters.tours) return;
    const ds = Array.isArray(t.tour_date) ? t.tour_date : t.tour_date ? [t.tour_date] : [];
    ds.forEach((x) => {
      const d = parseTourDate(x);
      if (d && d >= todayStart) upcoming.push({ date: d, kind: "tour", data: t });
    });
  });
  callouts.forEach((c) => {
    const d = c.date_iso ? parse(c.date_iso, "yyyy-MM-dd", new Date()) : parseTourDate(c.date);
    if (d && !isNaN(d) && d >= todayStart) upcoming.push({ date: d, kind: "callout", data: c });
  });
  upcoming.sort((a, b) => a.date - b.date);

  // Colored dots for a month day cell (red tour · purple possible tour · standby amber · neutral shift).
  const isPossible = (t) => isFutureRequest(t);
  const dayDots = (key) => {
    const arr = [];
    (shiftsByDay[key] || []).forEach((s) => arr.push(isTourShift(s) ? "bg-[#9B2226]" : isStandby(s) ? "bg-amber-400" : "bg-zinc-400"));
    (byDay[key] || []).forEach((t) => arr.push(isPossible(t) ? "bg-indigo-500" : "bg-[#9B2226]"));
    (calloutsByDay[key] || []).forEach(() => arr.push("bg-teal-400"));
    return arr;
  };

  // Small colored pill label for a call-out type, shown on all calendar views.
  const calloutPillMeta = (type) => {
    const t = type || "";
    if (t === "Sick") return { label: "Sick", cls: "border-orange-500/40 bg-orange-500/15 text-orange-300" };
    if (t.startsWith("FMLA")) return { label: "FMLA", cls: "border-[#9B2226]/50 bg-[#9B2226]/15 text-[#E0575B]" };
    if (t === "Vacation") return { label: "Vacation", cls: "border-sky-500/40 bg-sky-500/15 text-sky-300" };
    if (t === "ADO") return { label: "ADO", cls: "border-violet-500/40 bg-violet-500/15 text-violet-300" };
    if (t === "Late") return { label: "Late", cls: "border-amber-500/40 bg-amber-500/15 text-amber-300" };
    return { label: t || "Off", cls: "border-teal-500/40 bg-teal-500/15 text-teal-300" };
  };
  const CalloutPill = ({ c, prefix }) => {
    const m = calloutPillMeta(c.type);
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setCoEdit({ open: true, initial: c }); }}
        className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-700 uppercase tracking-wide transition-transform hover:scale-105 active:scale-95 ${m.cls}`}
        data-testid={`${prefix}-callout-${c.id}`}
      >
        {m.label}
      </button>
    );
  };
  const CalloutPills = ({ dayKey: dk, prefix }) => {
    const list = calloutsByDay[dk] || [];
    if (!list.length) return null;
    return <span className="flex flex-wrap items-center gap-1">{list.map((c) => <CalloutPill key={c.id} c={c} prefix={prefix} />)}</span>;
  };

  // ---- Consistent row helpers (shared by Day / Week / List) ----
  // "Day X of N" for a multi-day tour on a given calendar day.
  const tourDayInfo = (t, key) => {
    const dates = Array.isArray(t.tour_date) ? t.tour_date : (t.tour_date ? [t.tour_date] : []);
    const keys = dates.map((s) => { const d = parseTourDate(s); return d ? format(d, "yyyy-MM-dd") : null; }).filter(Boolean).sort();
    const total = keys.length || 1;
    const idx = keys.indexOf(key);
    return { idx: idx >= 0 ? idx + 1 : 1, total };
  };
  const StatusBadge = ({ t }) => isPossible(t)
    ? <span className="shrink-0 rounded bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-600 uppercase tracking-wide text-indigo-300">Request</span>
    : null;
  const DayTag = ({ t, dayKey: dk }) => {
    const { idx, total } = tourDayInfo(t, dk);
    return total > 1
      ? <span className="shrink-0 rounded bg-zinc-700/50 px-1.5 py-0.5 text-[10px] font-600 uppercase tracking-wide text-zinc-300">Day {idx} of {total}</span>
      : null;
  };
  // "Convert to tour" — same one-tap action as the Future Requests page, shown for any
  // tentative (possible) tour across every calendar view.
  const ConvertBtn = ({ t, prefix }) => (
    isPossible(t) && onConvert ? (
      <button
        onClick={(e) => { e.stopPropagation(); onConvert(t); }}
        className="inline-flex items-center gap-1 rounded-full border border-[#9B2226]/50 bg-[#9B2226]/10 px-2.5 py-1.5 text-[11px] font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20"
        data-testid={`${prefix}-convert-${t.id}`}
        title="Confirm a date and move this into your real schedule"
      >
        <CalendarCheck className="h-3.5 w-3.5" /> Convert to tour
      </button>
    ) : null
  );
  // Row 2: shift time · meet time · meet location · party size — same everywhere.
  // Future requests are tentative → shown as an all-day block (no times).
  const TourMeta = ({ sched, t }) => (
    isPossible(t) ? (
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-indigo-300/90">
        <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> All day · Request</span>
        {sched.meet_location && <span className="inline-flex items-center gap-1 text-zinc-400"><MapPin className="h-3 w-3" /> {sched.meet_location}</span>}
        {t.party_size ? <span className="inline-flex items-center gap-1 text-zinc-400"><Users className="h-3 w-3" /> {t.party_size}</span> : null}
      </div>
    ) : (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-400">
      {sched.start && <span className="inline-flex items-center gap-1 text-zinc-300"><Clock className="h-3 w-3" /> shift {sched.start}{sched.end ? `–${sched.end}` : ""}</span>}
      {sched.meet_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-zinc-500" /> meet {sched.meet_time}</span>}
      {sched.meet_location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {sched.meet_location}</span>}
      {t.party_size ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {t.party_size}</span> : null}
    </div>
    )
  );

  // Row 2 for a shift — shift time on its own line, matching the tour layout everywhere.
  const ShiftMeta = ({ s }) => (
    (s.start_time || s.end_time) ? (
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-400">
        <span className="inline-flex items-center gap-1 text-zinc-300"><Clock className="h-3 w-3" /> shift {s.start_time || ""}{s.end_time ? `–${s.end_time}` : ""}</span>
      </div>
    ) : null
  );

  // Unified shift box — identical structure in every view: all the info sits inside one
  // bordered box (like a tour), a discreet edit + delete sit beside it, and (only for
  // today's non-tour shift) the Call out button appears on its own row right below.
  const ShiftRow = ({ s, dayKey: dk, prefix, showConvert = false }) => {
    const isTour = isTourShift(s);
    const mdy = s.date || keyToMdy(dk);
    const showCallout = !isTour && isTodayShift(mdy);
    return (
      <div data-testid={`${prefix}-shift-${s.id}`}>
        <div className="flex items-stretch gap-1.5">
          <div className={`flex min-w-0 flex-1 flex-col items-start rounded-lg border px-3 py-2.5 text-sm ${isTour ? "border-[#9B2226]/30 bg-[#9B2226]/10" : "border-zinc-500/20 bg-zinc-500/10"}`}>
            <span className="flex w-full flex-wrap items-center gap-1.5 text-zinc-100">
              {isTour ? <Ticket className="h-4 w-4 shrink-0 text-[#E0575B]" /> : <Clock className="h-4 w-4 shrink-0 text-zinc-400" />}
              <span className="truncate font-500">{isTour ? cleanTourLabel(s) : (s.label || "Shift")}</span>
              {isTour
                ? <span className="shrink-0 rounded bg-[#9B2226]/15 px-1.5 py-0.5 text-[10px] font-600 uppercase tracking-wide text-[#E0575B]">Scheduled</span>
                : (isStandby(s) && <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-600 uppercase tracking-wide text-amber-300">Standby</span>)}
            </span>
            <ShiftMeta s={s} />
            {!isTour && s.note ? (
              <span className="note-preview mt-1 flex items-start gap-1 text-xs text-amber-300/90" data-testid={`${prefix}-note-preview-${s.id}`}>
                <StickyNote className="mt-0.5 h-3 w-3 shrink-0" /> <span className="whitespace-pre-wrap break-words">{s.note}</span>
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-center justify-center gap-2">
            {!isTour && (
              <button onClick={() => setNoteShift(s)} className={`flex shrink-0 items-center rounded-md px-1.5 transition-colors ${s.note ? "text-amber-400 hover:text-amber-300" : "text-zinc-500 hover:text-zinc-200"}`} data-testid={`${prefix}-note-${s.id}`} aria-label={s.note ? "Edit note" : "Add note"}>
                <StickyNote className="h-3.5 w-3.5" />
              </button>
            )}
            <EditBtn shift={s} testid={`${prefix}-edit-${s.id}`} />
            <DelBtn kind="shift" id={s.id} label={isTour ? cleanTourLabel(s) : (s.label || s.start_time || "this shift")} testid={`${prefix}-del-${s.id}`} />
          </div>
        </div>
        {(showConvert || showCallout) && (
          <div className="mt-1 flex items-center justify-end gap-2">
            {showConvert && (
              <button onClick={() => promoteShift(s.id)} className="inline-flex items-center gap-1 rounded-md border border-[#9B2226]/40 px-2 py-1 text-[11px] font-600 text-[#E0575B] transition-colors hover:bg-[#7A1A1E]/15" data-testid={`${prefix}-promote-${s.id}`}>
                {isTour ? "Open as tour" : "Convert to tour"}
              </button>
            )}
            {showCallout && <CallOutButton source={{ kind: "shift", id: s.id, label: s.label || "Shift", date: mdy, hours: hoursBetween(s.start_time, s.end_time) }} onSaved={reloadAll} testid={`${prefix}-callout-${s.id}`} />}
          </div>
        )}
      </div>
    );
  };


  // Shared day agenda (used under the month grid) — full tours + shifts for one day.
  const renderDayPanel = (key) => {
    const ds_shifts = shiftsByDay[key] || [];
    const ds_tours = byDay[key] || [];
    const ds_callouts = calloutsByDay[key] || [];
    if (!ds_shifts.length && !ds_tours.length && !ds_callouts.length) {
      return (
        <div className="py-2" data-testid="agenda-empty">
          <p className="mb-3 text-sm text-zinc-400">Nothing scheduled this day.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={openNewShift} className="h-9 border-zinc-700/50 bg-zinc-500/10 text-zinc-200 hover:bg-zinc-500/20" data-testid="day-quick-add-shift">
              <Plus className="mr-1.5 h-4 w-4" /> Add shift
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCoEdit({ open: true, initial: null, source: { date: keyToMdy(key) } })} className="h-9 border-zinc-700/50 bg-zinc-500/10 text-zinc-200 hover:bg-zinc-500/20" data-testid="day-quick-add-timeoff">
              <CalendarOff className="mr-1.5 h-4 w-4" /> Add time off
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {ds_callouts.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs uppercase tracking-wider text-teal-300">Time off / Call-outs</h4>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="day-callouts">
              {ds_callouts.map((c) => <CalloutPill key={c.id} c={c} prefix="day" />)}
            </div>
          </div>
        )}
        {ds_shifts.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs uppercase tracking-wider text-zinc-400">My Shifts</h4>
            <div className="space-y-2">
              {ds_shifts.map((s) => (
                <ShiftRow key={s.id} s={s} dayKey={key} prefix="day" showConvert />
              ))}
            </div>
          </div>
        )}
        {ds_tours.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs uppercase tracking-wider text-[#E0575B]">Tours</h4>
            <div className="space-y-2">
              {ds_tours.map((t) => {
                const ds = dayScheduleFor(t, key);
                const celebs = celebsForTour(t, parse(key, "yyyy-MM-dd", new Date()));
                return (
                  <div key={t.id}>
                    <div className="flex items-stretch gap-1.5">
                      <button onClick={() => navigate(`/guest/${t.id}`)} data-testid={`day-tour-${t.id}`} className={`min-w-0 flex-1 rounded-md border px-3 py-2 text-left transition-colors hover:bg-white/5 ${isPossible(t) ? "border-indigo-500/40 bg-indigo-500/10" : "border-zinc-800 bg-zinc-900/50"}`}>
                        <span className="flex flex-wrap items-center gap-1.5 font-500 text-zinc-100">
                          {celebs.length > 0 && <Cake className="h-3.5 w-3.5 shrink-0 text-amber-400" data-testid={`day-tour-cake-${t.id}`} />}
                          <span className="truncate">{tourTitle(t)}</span>
                          <StatusBadge t={t} />
                          <DayTag t={t} dayKey={key} />
                        </span>
                        {celebs.length > 0 && (
                          <span className="mt-1 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-600 text-amber-300" data-testid={`day-tour-celeb-${t.id}`}>
                            <Cake className="h-3 w-3" /> {celebs.map((c) => `${c.name}'s ${c.type === "anniversary" ? "anniversary" : "birthday"}`).join(" · ")}
                          </span>
                        )}
                        <TourMeta sched={ds} t={t} />
                      </button>
                      <DelBtn kind="tour" id={t.id} label={tourTitle(t)} testid={`day-del-tour-${t.id}`} />
                    </div>
                    {(isPossible(t) || isTodayShift(keyToMdy(key))) && (
                      <div className="mt-1 flex items-center justify-end gap-2">
                        <ConvertBtn t={t} prefix="day" />
                        {isTodayShift(keyToMdy(key)) && (
                          <CallOutButton source={{ kind: "tour", id: t.id, label: tourTitle(t), date: keyToMdy(key), multiDay: Array.isArray(t.tour_date) && t.tour_date.length > 1, hours: hoursBetween(ds.start, ds.end) }} onSaved={reloadAll} testid={`day-callout-tour-${t.id}`} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const title = view === "week"
    ? `${format(weekStart, "MMM d")} – ${format(addDays(weekStart, 6), "MMM d, yyyy")}`
    : view === "list" ? "Upcoming" : format(cursor, "MMMM yyyy");
  const step = (dir) => {
    if (view === "week") setCursor(dir > 0 ? addDays(cursor, 7) : subDays(cursor, 7));
    else setCursor(dir > 0 ? addMonths(cursor, 1) : subMonths(cursor, 1));
  };
  // "Today" jumps to the current month AND selects today's date (so the month
  // view lands on today and opens its day panel, not just the month).
  const goToToday = () => { const now = new Date(); setCursor(now); setDayKey(format(now, "yyyy-MM-dd")); };
  const navControls = (
    <div className="flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-900/40 p-0.5">
      <button onClick={() => step(-1)} className="rounded-md p-1.5 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-prev-btn" aria-label="Previous">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button onClick={goToToday} className="rounded-md px-2.5 py-1 text-xs font-500 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-today-btn">
        Today
      </button>
      <button onClick={() => step(1)} className="rounded-md p-1.5 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-next-btn" aria-label="Next">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
  // Swipe left/right to change month (touch devices).
  const onTouchStart = (e) => { const t = e.touches[0]; touchStart.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    const s = touchStart.current;
    touchStart.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
  };

  return (
    <div data-testid="calendar-view">
      <div className="mb-2.5 flex items-baseline justify-between gap-2">
        <h2 className="font-display text-lg font-800 tracking-tight text-zinc-50" data-testid="calendar-page-title">Calendar</h2>
        <span className="text-xs text-zinc-500">Tours, shifts &amp; requests</span>
      </div>
      {view === "list" && (
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-800 tracking-tight text-zinc-50 sm:text-3xl" data-testid="calendar-month-label">
            {title}
          </h2>
        </div>
      </div>
      )}

      {/* Toolbar: view switch + shift / import actions, grouped in a tidy panel */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/30 p-2" data-testid="calendar-toolbar">
        <div className="flex items-center gap-0.5 rounded-lg bg-zinc-950/60 p-0.5">
          {["month", "week", "list"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`calendar-view-${v}`}
              className={`rounded-md px-3 py-1.5 text-xs font-600 capitalize transition-colors ${view === v ? "bg-[#9B2226] text-white shadow-sm" : "text-zinc-400 hover:text-zinc-100"}`}
            >
              {v}
            </button>
          ))}
        </div>

        <div className="mx-0.5 hidden h-6 w-px bg-zinc-800 sm:block" />

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          data-testid="schedule-upload-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button
          size="sm"
          onClick={openNewShift}
          className="h-9 bg-[#9B2226] text-white hover:bg-[#7A1A1E]"
          data-testid="add-shift-btn"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add shift
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCoEdit({ open: true, initial: null, source: { date: keyToMdy(dayKey) } })}
          className="h-9 border-zinc-700/50 bg-zinc-500/10 text-zinc-300 hover:bg-zinc-500/20"
          data-testid="add-timeoff-btn"
          title="Log time off (Vacation, ADO, Sick, etc.)"
        >
          <CalendarOff className="mr-1.5 h-4 w-4" /> Add time off
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => !importing && inputRef.current?.click()}
          className="h-9 border-zinc-700/50 bg-zinc-500/10 text-zinc-300 hover:bg-zinc-500/20"
          data-testid="import-schedule-btn"
        >
          {importing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
          Import
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={undoImport}
          className="h-9 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
          data-testid="undo-import-btn"
          title="Undo the last schedule import"
        >
          <Undo2 className="mr-1.5 h-4 w-4" /> Undo import
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="calendar-filter-btn"
              className="flex h-9 items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/40 px-3 text-xs font-500 text-zinc-300 transition-colors hover:text-zinc-100 sm:ml-auto"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filter
              {activeFilterCount < 4 && <span className="ml-0.5 rounded-full bg-[#9B2226] px-1.5 py-0.5 text-[10px] font-700 text-white">{activeFilterCount}/4</span>}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 border-zinc-800 bg-zinc-950 text-zinc-200">
            <DropdownMenuLabel className="text-zinc-400">Show on calendar</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuCheckboxItem checked={filters.tours} onCheckedChange={(v) => setFilter("tours", !!v)} onSelect={(e) => e.preventDefault()} data-testid="filter-tours">
              <span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#9B2226]" /> Tours
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem checked={filters.requests} onCheckedChange={(v) => setFilter("requests", !!v)} onSelect={(e) => e.preventDefault()} data-testid="filter-requests">
              <span className="mr-2 inline-block h-2 w-2 rounded-full bg-indigo-500" /> Future requests
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem checked={filters.standby} onCheckedChange={(v) => setFilter("standby", !!v)} onSelect={(e) => e.preventDefault()} data-testid="filter-standby">
              <span className="mr-2 inline-block h-2 w-2 rounded-full bg-amber-400" /> Standby shifts
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem checked={filters.shifts} onCheckedChange={(v) => setFilter("shifts", !!v)} onSelect={(e) => e.preventDefault()} data-testid="filter-shifts">
              <span className="mr-2 inline-block h-2 w-2 rounded-full bg-zinc-400" /> Work shifts
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {shiftsLoaded && !tipDismissed && shifts.length === 0 && (
        <div className="relative mb-3 flex items-start gap-3 rounded-xl border border-zinc-600/40 bg-zinc-500/10 p-3.5" data-testid="calendar-first-tip">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-500/20 text-zinc-300"><Upload className="h-4 w-4" /></div>
          <div className="min-w-0 pr-6">
            <p className={`text-sm font-700 ${dark ? "text-zinc-50" : "text-zinc-900"}`}>First time here? Import your Disney schedule</p>
            <p className={`mt-0.5 text-xs ${dark ? "text-zinc-300" : "text-zinc-600"}`}>Screenshot your work schedule and upload it — the app reads it and auto-adds your shifts (and any tours) straight to this calendar.</p>
            <Button size="sm" onClick={() => !importing && inputRef.current?.click()} className="mt-2 h-8 bg-zinc-600 text-white hover:bg-zinc-700" data-testid="calendar-tip-upload">
              {importing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              Upload schedule screenshot
            </Button>
          </div>
          <button onClick={dismissTip} aria-label="Dismiss" data-testid="calendar-tip-dismiss" className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-500/10 hover:text-zinc-300">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {view === "month" && (
      <>
      <div className="mb-3 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-2.5" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="mb-1.5 flex items-center justify-between">
          <button onClick={() => step(-1)} className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-prev-btn" aria-label="Previous month"><ChevronLeft className="h-5 w-5" /></button>
          <div className="flex items-center gap-3">
            <span className="font-display text-lg font-700 text-zinc-100" data-testid="calendar-month-label">{format(cursor, "MMMM yyyy")}</span>
            <button onClick={goToToday} className="rounded-md px-2 py-0.5 text-sm font-600 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-today-btn">Today</button>
          </div>
          <button onClick={() => step(1)} className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-next-btn" aria-label="Next month"><ChevronRight className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-500 uppercase tracking-wider text-zinc-500">
          {["S", "M", "T", "W", "T", "F", "S"].map((w, i) => (
            <div key={i} className="py-1">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-month-grid">
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const inMonth = isSameMonth(day, cursor);
            const today = isSameDay(day, new Date());
            const selected = dayKey === key;
            const dots = dayDots(key);
            const hasCake = dayHasCelebration(key);
            return (
              <button
                key={key}
                data-testid={`calendar-day-${key}`}
                onClick={() => setDayKey(key)}
                className={`relative flex h-11 flex-col items-center justify-center rounded-xl text-sm transition-colors ${selected ? "bg-[#9B2226]/25 text-[#E0575B] ring-1 ring-[#E0575B]/70" : today ? "bg-[#9B2226]/10 text-zinc-100 ring-1 ring-[#9B2226]/50" : inMonth ? "text-zinc-200 hover:bg-zinc-800/70" : "text-zinc-700 hover:bg-zinc-800/40"}`}
              >
                {hasCake && (
                  <Cake className="absolute right-1.5 top-1.5 h-3 w-3 text-amber-400" data-testid={`calendar-cake-${key}`} aria-label="Celebration tour" />
                )}
                <span className={`leading-none ${today ? "font-800" : selected ? "font-700" : ""}`}>
                  {format(day, "d")}
                </span>
                <span className="absolute bottom-1.5 flex flex-wrap items-center justify-center gap-0.5 px-1">
                  {dots.slice(0, 4).map((c, i) => (
                    <span key={i} className={`h-1 w-1 rounded-full ${c}`} />
                  ))}
                  {dots.length > 4 && <span className="text-[8px] leading-none text-zinc-400">+{dots.length - 4}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid="month-day-agenda">
        <div className="mb-3">
          <h3 className="font-display text-sm font-700 text-zinc-100">
            {format(parse(dayKey, "yyyy-MM-dd", new Date()), "EEEE, MMMM d")}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px] text-zinc-400" data-testid="calendar-legend">
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#9B2226]" /> tour</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" /> request</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" /> standby</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400" /> shift</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><Cake className="h-3 w-3 shrink-0 text-amber-400" /> celebration</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" /> time off</span>
          </div>
        </div>
        {renderDayPanel(dayKey)}
      </div>
      </>
      )}

      {view === "week" && (
        <div className="space-y-1.5" data-testid="week-view" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="mb-1 flex items-center justify-between rounded-2xl border border-zinc-800 bg-zinc-900/40 px-2 py-2">
            <button onClick={() => step(-1)} className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-prev-btn" aria-label="Previous week"><ChevronLeft className="h-5 w-5" /></button>
            <div className="flex items-center gap-3">
              <span className="font-display text-base font-700 text-zinc-100" data-testid="calendar-week-label">{title}</span>
              <button onClick={goToToday} className="rounded-md px-2 py-0.5 text-sm font-600 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-today-btn">Today</button>
            </div>
            <button onClick={() => step(1)} className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100" data-testid="calendar-next-btn" aria-label="Next week"><ChevronRight className="h-5 w-5" /></button>
          </div>
          {weekDays.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayTours = byDay[key] || [];
            const dayShifts = shiftsByDay[key] || [];
            const dayCallouts = calloutsByDay[key] || [];
            const today = isSameDay(day, new Date());
            const empty = dayShifts.length === 0 && dayTours.length === 0 && dayCallouts.length === 0;
            return (
              <div key={key} data-testid={`week-day-${key}`} className={`rounded-lg border ${empty ? "px-3 py-1.5" : "p-2.5"} ${today ? "border-[#9B2226]/40 bg-[#9B2226]/5" : "border-zinc-800 bg-zinc-900/40"}`}>
                <div className={`flex items-center justify-between ${empty ? "" : "mb-1.5"}`}>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-display text-sm font-700 ${today ? "text-[#E0575B]" : "text-zinc-100"}`}>{format(day, "EEEE")}</span>
                    <span className="text-xs text-zinc-400">{format(day, "MMM d")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {empty && <span className="text-xs text-zinc-600">—</span>}
                    {today && <span className="rounded-full bg-[#9B2226] px-2 py-0.5 text-[10px] font-700 text-white">Today</span>}
                  </div>
                </div>
                {!empty && (
                  <div className="space-y-1.5">
                    {dayCallouts.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5" data-testid={`week-callouts-${key}`}>
                        {dayCallouts.map((c) => <CalloutPill key={c.id} c={c} prefix="week" />)}
                      </div>
                    )}
                    {dayShifts.map((s) => (
                      <ShiftRow key={s.id} s={s} dayKey={key} prefix="week" />
                    ))}
                    {dayTours.map((t) => {
                      const ds = dayScheduleFor(t, key);
                      const celebs = celebsForTour(t, parse(key, "yyyy-MM-dd", new Date()));
                      return (
                      <div key={t.id}>
                        <div className="flex items-stretch gap-1.5">
                          <button onClick={() => navigate(`/guest/${t.id}`)} data-testid={`week-tour-${t.id}`} className={`flex min-w-0 flex-1 flex-col items-start rounded-md border px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-white/5 ${isPossible(t) ? "border-indigo-500/40 bg-indigo-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                            <span className="flex w-full flex-wrap items-center gap-1.5 text-zinc-100"><Ticket className={`h-3.5 w-3.5 shrink-0 ${isPossible(t) ? "text-indigo-300" : "text-[#E0575B]"}`} /> <span className="truncate">{tourTitle(t)}</span>{celebs.length > 0 && <Cake className="h-3.5 w-3.5 shrink-0 text-amber-400" data-testid={`week-tour-cake-${t.id}`} />}<StatusBadge t={t} /><DayTag t={t} dayKey={key} /></span>
                            <TourMeta sched={ds} t={t} />
                          </button>
                          <DelBtn kind="tour" id={t.id} label={tourTitle(t)} testid={`week-del-tour-${t.id}`} />
                        </div>
                        {(isPossible(t) || isTodayShift(keyToMdy(key))) && (
                          <div className="mt-1 flex items-center justify-end gap-2">
                            <ConvertBtn t={t} prefix="week" />
                            {isTodayShift(keyToMdy(key)) && (
                              <CallOutButton source={{ kind: "tour", id: t.id, label: tourTitle(t), date: keyToMdy(key), multiDay: Array.isArray(t.tour_date) && t.tour_date.length > 1, hours: hoursBetween(ds.start, ds.end) }} onSaved={reloadAll} testid={`week-callout-tour-${t.id}`} />
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === "list" && (
        <div className="max-h-[72vh] space-y-1.5 overflow-y-auto pr-1" data-testid="list-view">
          {upcoming.length === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-10 text-center text-sm text-zinc-400" data-testid="list-empty">No upcoming shifts or tours.</div>
          ) : (
            upcoming.map((item, i) => {
              const key = format(item.date, "yyyy-MM-dd");
              const showHeader = i === 0 || format(upcoming[i - 1].date, "yyyy-MM-dd") !== key;
              return (
                <div key={item.kind + (item.data.id || "") + key + i}>
                  {showHeader && (
                    <div className="px-1 pb-1 pt-3 text-xs font-600 uppercase tracking-wider text-zinc-400">
                      {isToday(item.date) ? `Today · ${format(item.date, "EEE, MMM d")}` : format(item.date, "EEE, MMM d, yyyy")}
                    </div>
                  )}
                  {item.kind === "shift" ? (
                    <ShiftRow s={item.data} dayKey={key} prefix="list" />
                  ) : item.kind === "callout" ? (
                    <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2.5" data-testid={`list-callout-${item.data.id}`}>
                      <CalloutPill c={item.data} prefix="list" />
                      <span className="text-sm text-zinc-300">{item.data.type}{item.data.shift_hours ? ` · ${Math.round(item.data.shift_hours * 100) / 100}h` : ""}</span>
                    </div>
                  ) : (() => {
                    const sched = dayScheduleFor(item.data, key);
                    return (
                    <>
                    <div className="flex items-stretch gap-1.5">
                      <button onClick={() => navigate(`/guest/${item.data.id}`)} data-testid={`list-tour-${item.data.id}`} className={`flex min-w-0 flex-1 flex-col items-start rounded-lg border px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/5 ${isPossible(item.data) ? "border-indigo-500/40 bg-indigo-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                        <span className="flex w-full flex-wrap items-center gap-1.5 text-zinc-100"><Ticket className={`h-4 w-4 shrink-0 ${isPossible(item.data) ? "text-indigo-300" : "text-[#E0575B]"}`} /> <span className="truncate">{tourTitle(item.data)}</span><StatusBadge t={item.data} /><DayTag t={item.data} dayKey={key} /></span>
                        <TourMeta sched={sched} t={item.data} />
                      </button>
                      <DelBtn kind="tour" id={item.data.id} label={tourTitle(item.data)} testid={`list-del-tour-${item.data.id}`} />
                    </div>
                    {(isPossible(item.data) || isTodayShift(keyToMdy(key))) && (
                    <div className="mt-1 flex items-center justify-end gap-2">
                      <ConvertBtn t={item.data} prefix="list" />
                      {isTodayShift(keyToMdy(key)) && (
                        <CallOutButton source={{ kind: "tour", id: item.data.id, label: tourTitle(item.data), date: keyToMdy(key), multiDay: Array.isArray(item.data.tour_date) && item.data.tour_date.length > 1, hours: hoursBetween(sched.start, sched.end) }} onSaved={reloadAll} testid={`list-callout-tour-${item.data.id}`} />
                      )}
                    </div>
                    )}
                    </>
                    );
                  })()}
                </div>
              );
            })
          )}
        </div>
      )}

      <Dialog open={conflicts.length > 0} onOpenChange={(o) => !o && setConflicts([])}>
        <DialogContent className="max-w-lg border-zinc-800 bg-zinc-950" data-testid="import-conflicts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight text-amber-300">
              <AlertTriangle className="h-5 w-5" /> Kept your detailed tours
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-300">
            The schedule changed on these dates, but these tours already have details, so I kept them as-is instead of deleting them. Open one to adjust it, or Undo the import.
          </p>
          <div className="mt-2 max-h-72 space-y-2 overflow-y-auto">
            {conflicts.map((c) => (
              <button
                key={c.id}
                onClick={() => { setConflicts([]); navigate(`/guest/${c.id}`); }}
                className="flex w-full items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-left hover:bg-white/5"
                data-testid={`conflict-${c.id}`}
              >
                <span className="font-500 text-zinc-100">{c.name}</span>
                <span className="text-xs text-zinc-400">{c.date}</span>
              </button>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { undoImport(); setConflicts([]); }} data-testid="conflicts-undo-btn">Undo import</Button>
            <Button className="bg-zinc-600 text-white hover:bg-zinc-700" onClick={() => setConflicts([])} data-testid="conflicts-ok-btn">Keep them</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="calendar-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {confirmDel?.kind === "shift" ? "shift" : "tour"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel?.label ? <span className="font-500 text-zinc-300">{confirmDel.label}</span> : null}
              {confirmDel?.kind === "shift"
                ? " will be permanently removed. This can't be undone."
                : " will be moved to Trash. You can restore it from Settings within 30 days."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="calendar-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 text-white hover:bg-red-500" data-testid="calendar-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShiftEditor
        open={shiftEditorOpen}
        onOpenChange={setShiftEditorOpen}
        shift={editingShift}
        defaultDate={dayKey}
        onSaved={onShiftSaved}
      />

      <ShiftNoteDialog
        shift={noteShift}
        onOpenChange={(o) => !o && setNoteShift(null)}
        onSaved={reloadAll}
      />

      <CallOutDialog
        open={coEdit.open}
        onOpenChange={(o) => setCoEdit((s) => ({ ...s, open: o }))}
        initial={coEdit.initial}
        source={coEdit.source}
        onSaved={reloadAll}
      />
      <ScheduleReviewDialog
        open={review.open}
        onOpenChange={(o) => setReview((s) => ({ ...s, open: o }))}
        changes={review.changes}
        mode={review.mode}
        busy={reviewBusy}
        onApply={applyPendingSchedule}
      />
    </div>
  );
};
